import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ICONS from "../../../../assets/Icons";
import { getCreateTransactionBalance } from "../../../../redux/features/owner/ownerAllVerification/ownerAllVerification";
import accountNameStandardised from "../../../../utils/accountNamestandardised";
import { GlobalConfiguration } from "../../../../utils/configurationProvider";
import formatTotalBalance from "../../../../utils/formatTotalBalance";
import TEXT from "../../../../utils/globalText";
import UniversalRefreshToast from "../../../_utils/UniversalRefreshToast";
import AnimationUpAndDown from "../../../BeneTransaction/OwnerPopup/AnimationUpAndDown";
import Loader from "../../../Loader/Index";
import useServiceChecker from "../../../ServiceChecker";
import CommonBankLogo from "../../CommonBankLogo";
import IconTearDropButton from "../../CommonButtons/IconTearDropButton";
import PrimaryWithIconButton from "../../CommonButtons/PrimaryWithIconButton";
import CommonToast from "../../CommonToast";
import CardActionableButton from "../../CommonViewAllCard/CardSubComponent/CardActionableButton";
import { formatAccountNumber } from "../../FormatAccountNumber";
import TickerAnimation from "../../TickerAnimation";
import CommonAccountModal from "./CommonAccountModal";
import CommonAlertCard from "../../CommonAlertCard";
// Suggestions of props for component while using the components -------------------------------------
/**
 * @param {String} props.type - passs type like "source" or "destination"
 * @param {String} props.flow - If type is "destination" or "internal", then pass "beneficiary" or "internal"
 * @param {String} props.page - If you want this component on confirm page then, pass "confirm"
 * @param {Boolean} props.modalOpen - Is modal open or not
 * @param {Object} props.setModalOpen - Setter of modal state ( true or false )
 * @param {Object} props.txnPayLoad - txnPayload array
 * @param {Object} props.setTxnPayLoad - Setter of txnPayload
 * @param {Boolean} props.shouldShowAssociatedAccounts -  If you are using type = "destination", then pass state of showing associted accounts found
 * @param {Function} props.handleOnAccountSelection - If you are using type = "destination", then pass handle function of Associate account found
 * @returns {JSX.Element}
 */

const SourceDestinationAccountDetail = ({
  type,
  flow,
  page,
  modalOpen,
  setModalOpen,
  txnPayLoad,
  setTxnPayLoad,
  shouldShowAssociatedAccounts,
  handleOnAccountSelection,
  isDisabled,
  isEditDisabled,
  isReimbursement,
  activeType,
  setActiveType,
  setRemPan,
  setRemAccount,
  setRemIFSC,
  remIFSC,
  remAccount,
  remPan,
  TypeRTGS,
  currentAccountDetails,
  isFieldforce,
  flowType,
}) => {
  const shouldBeRedacted = (price) => {
    return (
      price === "xxxx.xx" ||
      isNaN(+price) ||
      price === null ||
      price === undefined
    );
  };
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const services = useSelector((state) => state.refreshToken.services);
  const [associateModalOpen, setAssociateModalOpen] = useState(false);
  const requestActiveRef = useRef(false);
  const toastIdRef = useRef(null);
  const completedRef = useRef(false);
  const hasSeenLoadingRef = useRef(false);
  const [ensureAll] = useServiceChecker();

  //states, variables and logics for Source account (starts) ---------------------------------
  const [balanceRefresh, setBalanceRefresh] = useState(false);
  const [internalBalanceRefresh, setinternalBalanceRefresh] = useState(false);
  const [refreshAnimationId, setRefreshAnimationId] = useState(null);
  const [previousRefreshData, setPreviousRefreshData] = useState(null);
  const [balanceRefreshDisabled, setBalanceRefreshDisabled] = useState(false);
  const [refreshType, setRefreshType] = useState(false);
  const [transactionFilterData, setTransactionFilterData] = useState([]);
  const {
    createTransactionBankRefreshLoading,
    createTransactionBankRefreshError,
    createTransactionBankRefreshData,
  } = useSelector((state) => state.ownerAllVerification);

  const { beneOwnerData } =
    useSelector((state) => state.beneTransactionAccounts) || {};

  useEffect(() => {
    setTransactionFilterData(beneOwnerData?.owner_accounts);
  }, [beneOwnerData]);

  const cleanupAfterRefresh = () => {
    toastIdRef.current = null;
    requestActiveRef.current = false;
    completedRef.current = false;
    hasSeenLoadingRef.current = false;

    setBalanceRefreshDisabled(false);
  };

  useEffect(() => {
    // Track when loading starts to ensure we only process errors from actual API calls
    if (createTransactionBankRefreshLoading && requestActiveRef.current) {
      hasSeenLoadingRef.current = true;
      if (!toastIdRef.current) {
        UniversalRefreshToast();
      }
      return;
    }

    // Ignore anything after completion or if no request was made
    if (!requestActiveRef.current || completedRef.current) return;

    // Only process success/error if we've seen loading start (to avoid stale errors)
    if (!hasSeenLoadingRef.current) return;

    if (
      !createTransactionBankRefreshLoading &&
      createTransactionBankRefreshData?.status
    ) {
      completedRef.current = true;

      toast.dismiss(toastIdRef.current);

      CommonToast({
        text: "Balance refreshed successfully.",
        icon: ICONS?.SELECTED_WITH_WHITE_FILLED,
        variant: "var(--success)",
        btnProps: {
          isDisabled: false,
          enabledTextColor: "var(--text-primary)",
          enabledBgColor: "var(--primary-bg)",
        },
      });

      cleanupAfterRefresh();
      return;
    }

    // Only show error if a request was actually made and we've seen loading
    if (
      !createTransactionBankRefreshLoading &&
      createTransactionBankRefreshError &&
      requestActiveRef.current &&
      hasSeenLoadingRef.current
    ) {
      completedRef.current = true;

      toast.dismiss(toastIdRef.current);
      CommonToast({
        text: createTransactionBankRefreshError,
        icon: ICONS?.INFO_FILLED,
        variant: "var(--error)",
      });

      cleanupAfterRefresh();
    }
  }, [
    createTransactionBankRefreshLoading,
    createTransactionBankRefreshData,
    createTransactionBankRefreshError,
  ]);

  const handleRefresh = async (e, id, prevBalance, internalAccount) => {
    e.stopPropagation();

    if (
      !(services?.includes("VAccountBalance") || services?.includes("Admin"))
    ) {
      navigate("/feature_not_assigned", {
        state: {
          unavailableService: ["VAccountBalance", "Admin"],
        },
      });
      return;
    }
    setinternalBalanceRefresh(true);

    if (requestActiveRef.current) return;

    requestActiveRef.current = true;
    completedRef.current = false;
    toastIdRef.current = null;

    setBalanceRefreshDisabled(true);

    setPreviousRefreshData(prevBalance);
    setRefreshType("single");
    setRefreshAnimationId(id);

    let response = await dispatch(getCreateTransactionBalance(id));
    setinternalBalanceRefresh(false);
    if (response?.payload?.status === true) {
      if (internalAccount) {
        setTxnPayLoad((prev) => ({
          ...prev,
          destinationAcountOwnerLastUpdated:
            response.payload?.owner_accounts?.[0]
              ?.owner_account_balance_last_fetched_at_in_words,
          destinationAccountBalance:
            response.payload?.owner_accounts?.[0]?.owner_account_balance,
        }));
      } else {
        setTxnPayLoad((prev) => ({
          ...prev,
          ownerBankBalance:
            response.payload?.owner_accounts?.[0]?.owner_account_balance,
          ownerLastUpdated:
            response.payload?.owner_accounts?.[0]
              ?.owner_account_balance_last_fetched_at_in_words,
        }));
      }
    }
  };

  //states, variables and logics  for Source account (Ends) ---------------------------------

  const handleOpenModal = () => {
    if (!isEditDisabled) {
      if (page !== "confirm") {
        if (type === "destination") {
          setActiveType?.("pan");
          setRemPan?.("");
          setRemAccount?.("");
          setRemIFSC?.("");
        }
        setModalOpen(true);
      }
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleAssociateOpenModal = () => {
    setRemPan(txnPayLoad?.pan);
    setAssociateModalOpen(true);
  };

  const handleAssociateCloseModal = () => {
    setAssociateModalOpen(false);
  };

  const registerNewAccount = () => {
    if (isFieldforce) {
      CommonToast({
        text: "Feature coming soon",
        icon: "https://q2pay.in/new_assets/common/detail_page/info.svg",
        variant: "var(--coming-soon)",
        buttonNeeded: false,
      });
    } else {
      navigate("/beneficiary_account_dashboard/create_beneficiary_account");
    }
  };

  const hasBUAccountService = services?.includes("BUAccount");

  const service =
    ensureAll(["VAccountBalance"]).status || ensureAll(["Admin"]).status;
  return (
    <>
      {modalOpen && (type === "destination" || flow === "internal") && (
        <div className="fixed top-top-base left-left-base w-full-width min-h-full-height flex items-center justify-center z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <CommonAccountModal
            onClose={handleCloseModal}
            sourceDestiAcc={flow === "beneficiary" ? true : false}
            onAccountSelect={handleOnAccountSelection}
            accountNumber={txnPayLoad?.bankAccountNo}
            IFSC={txnPayLoad?.bankAccountIFSC}
            setTxnPayLoad={setTxnPayLoad}
            txnPayLoad={txnPayLoad}
            flow={flow === "beneficiary" ? "beneTxn" : "internalTxn"}
            flowType={flowType}
            activeType={activeType}
            setActiveType={setActiveType}
            setRemPan={setRemPan}
            setRemAccount={setRemAccount}
            setRemIFSC={setRemIFSC}
            remIFSC={remIFSC}
            remAccount={remAccount}
            remPan={remPan}
            TypeRTGS={TypeRTGS}
            currentAccountDetails={currentAccountDetails}
          />
        </div>
      )}

      {modalOpen && type === "source" && (
        <div className="fixed top-top-base left-left-base w-full-width min-h-full-height flex items-center justify-center z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <CommonAccountModal
            onAccountSelect={handleOnAccountSelection}
            accountNumber={txnPayLoad?.bankAccountNo}
            IFSC={txnPayLoad?.bankAccountIFSC}
            setTxnPayLoad={setTxnPayLoad}
            txnPayLoad={txnPayLoad}
            type={"source"}
            setOpenOwner={setModalOpen}
            TypeRTGS={TypeRTGS}
            currentAccountDetails={currentAccountDetails}
          />
        </div>
      )}
      {associateModalOpen && (
        <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <CommonAccountModal
            sourceDestiAcc={false}
            onClose={handleAssociateCloseModal}
            onAccountSelect={handleOnAccountSelection}
            accountNumber={txnPayLoad?.bankAccountNo}
            IFSC={txnPayLoad?.bankAccountIFSC}
            panNumber={txnPayLoad?.pan}
            setTxnPayLoad={setTxnPayLoad}
            txnPayLoad={txnPayLoad}
            BeneTxnAssocAcct={true}
            setAssociateModalOpen={setAssociateModalOpen}
            flow={flow === "internal" ? "" : "beneTxn"}
            accountType={flow === "internal" ?? "internal"}
            associateTxn={flow === "internal" ?? "associateTxn"}
            ispanbankRequired={true}
            activeType={activeType}
            setActiveType={setActiveType}
            setRemPan={setRemPan}
            setRemAccount={setRemAccount}
            setRemIFSC={setRemIFSC}
            remIFSC={remIFSC}
            remAccount={remAccount}
            remPan={remPan}
            associateModalOpen={associateModalOpen}
          />
        </div>
      )}

      <div className="w-width-full">
        <div
          className={`flex flex-col relative rounded-border-radius-md ${shouldShowAssociatedAccounts && "gap-gap-md"}`}
        >
          <div
            className={`bg-primary-nav flex flex-col relative rounded-border-radius-md ${isDisabled ? "opacity-light-opacity cursor-not-allowed pointer-events-none" : ""}`}
          >
            <div
              className={`p-padding-md h-height-full bg-primary-card flex flex-col rounded-border-radius-md`}
            >
              {page !== "confirm" &&
              ((txnPayLoad?.bankAccountNo &&
                txnPayLoad?.bankAccountIFSC &&
                type !== "source") ||
                (txnPayLoad?.ownerBankAccountNo &&
                  txnPayLoad?.ownerBankIFSC &&
                  type === "source")) ? (
                <div className="top-top-base right-right-base absolute">
                  <IconTearDropButton
                    handleFunction={handleOpenModal}
                    isDisabled={isEditDisabled || false}
                    isLoading={false}
                    enabledBgColor={"var(--primary-nav-act)"}
                    icon={ICONS?.EDIT}
                    hoverOption="white"
                    title={TEXT?.EDIT}
                  />
                </div>
              ) : null}
              <div
                className={`flex flex-col gap-gap-md ${(txnPayLoad?.bankAccountNo && txnPayLoad?.bankAccountIFSC && type !== "source") || (txnPayLoad?.ownerBankAccountNo && txnPayLoad?.ownerBankIFSC && type === "source") ? " mb-margin-xl" : ""}`}
              >
                <div className="flex flex-col gap-gap-xs">
                  <span className="text-font-md text-text-primary font-semibold">
                    {type === "source"
                      ? TEXT?.SOURCE_ACCOUNT_CAPITALIZE
                      : TEXT?.DESTINATION_ACCOUNT_CAPITALIZE}
                  </span>
                  <span className="text-xs text-text-secondary">
                    {type === "source"
                      ? TEXT?.SOURCE_ACCOUNT_DES_TEXT
                      : TEXT?.DESTINATION_ACCOUNT_DES_TEXT}
                  </span>
                </div>

                {(!txnPayLoad?.bankAccountNo &&
                  !txnPayLoad?.bankAccountIFSC &&
                  type !== "source") ||
                (!txnPayLoad?.ownerBankAccountNo &&
                  !txnPayLoad?.ownerBankIFSC &&
                  type === "source") ? (
                  <div>
                    <PrimaryWithIconButton
                      btnText={
                        isReimbursement
                          ? TEXT?.SELECT_REIMBURSEMENT_ACCOUNT
                          : type === "source"
                            ? TEXT?.SELECT_OWNER_ACCOUNT
                            : type === "destination" &&
                                (flow === "internal" || flow === "internalTxn")
                              ? TEXT?.SELECT_DESTINATION_ACCOUNT
                              : isFieldforce
                                ? TEXT?.SELECT_FIELD_FORCE_ACCOUNT
                                : TEXT?.SELECT_BENEFICIARY_ACCOUNT
                      }
                      enabledBgColor="var(--primary-nav-act)"
                      isDisabled={false}
                      handleFunction={handleOpenModal}
                      iconToBeAlign="left"
                      enabledTextColor={"var(--text-primary)"}
                      icon={ICONS?.SELECT_ACCOUNT}
                      fullWidthRequired={true}
                      hoverOption="white"
                    />
                    {type === "destination" &&
                      flow === "beneficiary" &&
                      !txnPayLoad?.bankAccountNo &&
                      !txnPayLoad?.bankAccountIFSC && (
                        <div className="mt-margin-md">
                          <PrimaryWithIconButton
                            btnText={TEXT?.REGISTER_NEW_ACCOUNT}
                            enabledBgColor="var(--primary-nav-act)"
                            handleFunction={registerNewAccount}
                            isDisabled={!hasBUAccountService}
                            iconToBeAlign="left"
                            enabledTextColor="var(--text-primary)"
                            icon={ICONS?.ADD_BUTTON}
                            imgColor="var(--text-primary)"
                            fullWidthRequired
                            hoverOption="white"
                          />
                        </div>
                      )}
                  </div>
                ) : (txnPayLoad?.bankAccountNo &&
                    txnPayLoad?.bankAccountIFSC &&
                    type !== "source") ||
                  (txnPayLoad?.ownerBankAccountNo &&
                    txnPayLoad?.ownerBankIFSC &&
                    type === "source") ? (
                  <>
                    <div className="flex flex-col gap-gap-md">
                      <div className="flex flex-col w-width-full h-auto py-padding-sm px-padding-md bg-primary-nav rounded-border-radius-md gap-gap-xs">
                        <span className="text-sm text-text-primary font-semibold">
                          {type === "source"
                            ? formatAccountNumber(
                                txnPayLoad?.ownerBankAccountNo,
                              )
                            : formatAccountNumber(txnPayLoad?.bankAccountNo)}
                        </span>
                        <span className="text-xs text-text-secondary">
                          {TEXT?.ACCOUNT_NUMBER}
                        </span>
                      </div>
                      {type !== "source" && flow !== "internal" ? (
                        <div className="flex flex-col w-width-full h-auto py-padding-sm px-padding-md bg-primary-nav rounded-border-radius-md gap-gap-xs">
                          <div className="text-sm text-text-primary font-semibold">
                            <div className="flex-1 truncate flex">
                              <TickerAnimation
                                text={accountNameStandardised(
                                  txnPayLoad?.bankAccountName,
                                )}
                              />
                            </div>
                          </div>
                          <span className="text-xs text-text-secondary">
                            {TEXT?.ACCOUNT_NAME}
                          </span>
                        </div>
                      ) : (
                        <div className="flex flex-col Qlg:flex-row w-width-full h-auto py-padding-sm px-padding-md bg-primary-nav rounded-border-radius-md Qlg:gap-gap-xs gap-gap-base justify-between">
                          <div className="flex flex-1 flex-col gap-gap-xs">
                            <span className="text-sm text-text-primary font-semibold">
                              {flow === "internal"
                                ? txnPayLoad?.bankAccountIFSC
                                : txnPayLoad?.ownerBankIFSC}
                            </span>
                            <span className="text-xs text-text-secondary">
                              {TEXT?.IFSC_CODE}
                            </span>
                          </div>
                          <div className="flex flex-col Qlg:flex-row flex-1 Qlg:gap-gap-sm gap-gap-md w-width-full Qlg:w-width-md">
                            <div className="border-l-border border-l-primary-bg"></div>

                            {flow === "internal" ? (
                              <div className="w-width-full relative">
                                <div className="flex flex-col gap-gap-xs">
                                  <div className="flex items-center gap-gap-sm relative">
                                    <div
                                      style={{
                                        color:
                                          txnPayLoad?.destinationAccountBalance
                                            ? Number(
                                                txnPayLoad?.destinationAccountBalance,
                                              ) <
                                              GlobalConfiguration.lengthCheckZero
                                              ? "var(--failed)"
                                              : "var(--settled)"
                                            : "var(--text-primary)",
                                      }}
                                      className="text-left font-semibold flex text-sm "
                                    >
                                      {txnPayLoad?.destinationAccountBalance
                                        ? `
                                  ${shouldBeRedacted(txnPayLoad.destinationAccountBalance) ? "" : "INR "}
                              ${
                                formatTotalBalance(
                                  Number(txnPayLoad?.destinationAccountBalance),
                                  "Default",
                                ).amount
                              }
                              ${formatTotalBalance(Number(txnPayLoad?.destinationAccountBalance), "Default").abbr}
                          `
                                        : "Not Available"}
                                      <div
                                        className={`relative w-icon-xs h-icon-xs`}
                                      >
                                        <AnimationUpAndDown
                                          loader={
                                            createTransactionBankRefreshLoading
                                          }
                                          currentId={
                                            txnPayLoad?.destinationAcountOwnerId
                                          }
                                          previousId={refreshAnimationId}
                                          currentBalance={
                                            txnPayLoad?.destinationAccountBalance
                                          }
                                          previousBalance={previousRefreshData}
                                          refreshType={refreshType}
                                          classType={"sideDrawer"}
                                        />
                                      </div>
                                    </div>

                                    {internalBalanceRefresh ? (
                                      <>
                                        <div className="border-l-border border-l-primary-bg h-icon-sm"></div>
                                        <Loader
                                          height="var(--icon-sm)"
                                          width="var(--icon-sm)"
                                        />
                                      </>
                                    ) : txnPayLoad?.isDestinationOwnerActive ? (
                                      service && (
                                        <>
                                          <div className="border-l-border border-l-primary-bg h-icon-sm"></div>
                                          <CardActionableButton
                                            btnType="button"
                                            handleFunction={(e) => {
                                              handleRefresh(
                                                e,
                                                txnPayLoad?.destinationAcountOwnerId,
                                                txnPayLoad?.destinationAccountBalance,
                                                true,
                                              );
                                            }}
                                            isDisabled={
                                              ensureAll(["VAccountBalance"])
                                                .status ||
                                              ensureAll(["Admin"]).status
                                                ? false
                                                : true
                                            }
                                            disabledBgColor="var(--primary-nav-act)"
                                            icon="https://q2pay.in/new_assets/common/view_all/reload_refresh_white.svg"
                                            hoverOption="white"
                                            title="Refresh"
                                          />
                                        </>
                                      )
                                    ) : null}
                                  </div>
                                  <div className="text-left text-xs text-text-secondary whitespace-nowrap">
                                    <div className="w-full max-w-full overflow-hidden">
                                      <TickerAnimation
                                        text={
                                          txnPayLoad?.isDestinationOwnerActive
                                            ? `${
                                                txnPayLoad?.destinationAccountBalance
                                                  ? formatTotalBalance(
                                                      txnPayLoad?.destinationAccountBalance,
                                                      "Default",
                                                    ).prefix === "crores"
                                                    ? `Balance in ${
                                                        formatTotalBalance(
                                                          txnPayLoad?.destinationAccountBalance,
                                                          "Default",
                                                        ).prefix
                                                      } - `
                                                    : TEXT.BALANCE_UPDATED
                                                  : TEXT.BALANCE_UPDATED
                                              } ${txnPayLoad?.destinationAcountOwnerLastUpdated}`
                                            : "Balance"
                                        }
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="w-width-full">
                                <div className="flex flex-col gap-gap-xs">
                                  <div className="flex items-center gap-gap-sm relative">
                                    <div
                                      className={`text-left  text-sm 
                                     ${
                                       txnPayLoad?.ownerBankBalance
                                         ? txnPayLoad?.ownerBankBalance <
                                             GlobalConfiguration?.lengthCheckZero &&
                                           !(
                                             isNaN(
                                               txnPayLoad?.ownerBankBalance,
                                             ) ||
                                             txnPayLoad?.ownerBankBalance ===
                                               "xxxx.xx" ||
                                             txnPayLoad?.ownerBankBalance ===
                                               null ||
                                             txnPayLoad?.ownerBankBalance ===
                                               undefined
                                           )
                                           ? "text-failed font-semibold"
                                           : "text-settled font-semibold"
                                         : "text-text-primary font-normal"
                                     }
                                    `}
                                    >
                                      {shouldBeRedacted(
                                        txnPayLoad?.ownerBankBalance,
                                      )
                                        ? ""
                                        : "INR "}
                                      {(txnPayLoad?.ownerBankBalance ??
                                      formatTotalBalance(
                                        txnPayLoad?.ownerBankBalance,
                                        "Default",
                                      ).prefix === "crores")
                                        ? formatTotalBalance(
                                            txnPayLoad?.ownerBankBalance,
                                            "crores",
                                          ).amount
                                        : formatTotalBalance(
                                            txnPayLoad?.ownerBankBalance,
                                            "Default",
                                          ).amount}
                                    </div>

                                    <div
                                      className={`relative w-icon-xs h-icon-xs`}
                                    >
                                      <AnimationUpAndDown
                                        loader={
                                          createTransactionBankRefreshLoading
                                        }
                                        currentId={txnPayLoad?.ownerId}
                                        previousId={refreshAnimationId}
                                        currentBalance={
                                          txnPayLoad?.ownerBankBalance
                                        }
                                        previousBalance={previousRefreshData}
                                        refreshType={refreshType}
                                        classType={"sourceDestination"}
                                      />
                                    </div>

                                    {service && (
                                      <>
                                        <div className="border-l-border border-l-primary-bg h-icon-sm"></div>
                                        {(createTransactionBankRefreshLoading ||
                                          balanceRefresh) &&
                                        !internalBalanceRefresh ? (
                                          <Loader
                                            height="var(--icon-sm)"
                                            width="var(--icon-sm)"
                                          />
                                        ) : (
                                          <CardActionableButton
                                            btnType="button"
                                            handleFunction={(e) => {
                                              handleRefresh(
                                                e,
                                                txnPayLoad?.ownerId,
                                                txnPayLoad?.ownerBankBalance,
                                              );
                                            }}
                                            isDisabled={
                                              ensureAll(["VAccountBalance"])
                                                .status ||
                                              ensureAll(["Admin"]).status
                                                ? false
                                                : true
                                            }
                                            disabledBgColor="var(--primary-nav-act)"
                                            icon={ICONS?.RELOAD_REFRESH_WHITE}
                                            hoverOption="white"
                                            title="Refresh"
                                          />
                                        )}
                                      </>
                                    )}
                                  </div>
                                  <div className="text-left text-xs text-text-secondary whitespace-nowrap">
                                    <div className="w-full max-w-full overflow-hidden">
                                      <TickerAnimation
                                        text={`${
                                          txnPayLoad?.ownerBankBalance
                                            ? formatTotalBalance(
                                                txnPayLoad?.ownerBankBalance,
                                                "Default",
                                              ).prefix === "crores"
                                              ? `Balance in ${
                                                  formatTotalBalance(
                                                    txnPayLoad?.ownerBankBalance,
                                                    "Default",
                                                  ).prefix
                                                } - `
                                              : TEXT.BALANCE_UPDATED
                                            : TEXT.BALANCE_UPDATED
                                        } ${txnPayLoad?.ownerLastUpdated}`}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between absolute right-right-base bottom-bottom-base">
                      <div className="bg-text-primary rounded-tl-border-radius-md rounded-br-border-radius-md flex items-center justify-around w-width-full h-full py-padding-sm px-padding-md">
                        <CommonBankLogo
                          ifsc={
                            type === "destination"
                              ? txnPayLoad?.bankAccountIFSC
                              : txnPayLoad?.ownerBankIFSC
                          }
                          variant="big"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <></>
                )}
              </div>
            </div>
          </div>
          <div>
            <div className="w-width-full flex flex-col gap-gap-md">
              {shouldShowAssociatedAccounts &&
                type === "destination" &&
                (flow === "beneficiary" || flow === "internal") && (
                  <CommonAlertCard
                    title={TEXT?.ACTIVE_ASSOCIATED_ACCOUNTS}
                    texts={
                      flow === "beneficiary"
                        ? TEXT?.OTHER_EXISTING_BENEFICIARY_ACCOUNTS_FOUND
                        : TEXT?.OTHER_EXISTING_INTERNAL_ACCOUNTS_FOUND
                    }
                    onClick={handleAssociateOpenModal}
                  />
                )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SourceDestinationAccountDetail;
